import { useFocusRing } from '@react-native-aria/focus'
import React, { useState, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { NativeSyntheticEvent } from 'react-native'
import { TextInput, View } from 'react-native'

import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { cn } from '@/src/general/utils/cn'
import { containsOnlyNumbers } from '@/src/general/utils/containsOnlyNumbers'
import { removeSpaces } from '@/src/general/utils/removeSpaces'

const EMAIL_CONFIRMATION_CODE_MAX_LENGTH = 6

export type TCodeInputProps = {
  value: string
  onChangeText: (code: string) => void
  onSubmit: (code: string) => Promise<void> | void
  isError?: boolean
  errorMessage?: ReactNode
}

export const CodeInput = ({ value, onChangeText, onSubmit, isError = false, errorMessage }: TCodeInputProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const { formatMessage } = useIntl()

  const { isFocusVisible, focusProps } = useFocusRing()
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- useFocusRing focusProps typing
  const { onBlur: focusRingOnBlur, onFocus: focusRingOnFocus } = focusProps as {
    onBlur?: (e: NativeSyntheticEvent<unknown>) => void
    onFocus?: (e: NativeSyntheticEvent<unknown>) => void
  }

  const codeDigitsArray = Array.from({ length: EMAIL_CONFIRMATION_CODE_MAX_LENGTH + 1 }).fill(0)

  const renderDigitInput = (_: unknown, index: number) => {
    if (index === 3) {
      return (
        <Typography key={index} size="3xl" className="mx-2.5 text-[#3F2B1829]">
          -
        </Typography>
      )
    }

    // The display includes a separator at index 3, but code contains only digits.
    // Shift every index after the separator back by one to match its position in code.
    const currentIndex = index < 3 ? index : index - 1
    const emptyInput = ' '
    const digit = value[currentIndex] || emptyInput

    return (
      <View
        key={index}
        dataSet={{
          'focus-visible':
            currentIndex === Math.min(value.length, EMAIL_CONFIRMATION_CODE_MAX_LENGTH - 1) && isFocusVisible,
        }}
        className={cn(
          'mr-2 w-10 h-30 items-center bg-default-bg-light border border-layout-divider-contrast-high  rounded-md py-2',
          index === 2 && 'mr-0',
          'outline-none web:data-[focus-visible=true]:outline-4 web:data-[focus-visible=true]:outline-offset-1 web:data-[focus-visible=true]:outline-layout-focus',
          currentIndex === Math.min(value.length, EMAIL_CONFIRMATION_CODE_MAX_LENGTH - 1) &&
            isFocused &&
            !isFocusVisible &&
            'border-default-input-focus',
        )}
      >
        <Typography size="3xl" className="h-10">
          {digit}
        </Typography>
      </View>
    )
  }

  // Change text only if it's numbers, and remove spaces for copy/paste of emailed code
  // props "123 456" -> set code to 123456
  // props "1 text" -> doesn't set
  const handleTextChange = (text: string) => {
    if (!text) onChangeText('')
    else {
      const trimmedText = removeSpaces(text)
      if (containsOnlyNumbers(trimmedText)) onChangeText(trimmedText)
    }
  }

  return (
    <View className="flex flex-col items-start justify-center py-5">
      <View className="flex-1 relative">
        <View className="flex-row items-center justify-between">{codeDigitsArray.map(renderDigitInput)}</View>
        <TextInput
          testID={TEST_ID.MISCELLANEOUS.CODE_INPUT}
          className="absolute inset-0 text-transparent outline-none"
          keyboardType="numeric"
          returnKeyType="none"
          textContentType="oneTimeCode"
          accessibilityLabel={formatMessage({ id: 'auth.otp.code.accessibility-label' })}
          onChangeText={handleTextChange}
          value={value}
          maxLength={EMAIL_CONFIRMATION_CODE_MAX_LENGTH}
          onSubmitEditing={() => onSubmit(value)}
          onFocus={(e) => {
            setIsFocused(true)
            focusRingOnFocus?.(e)
          }}
          onBlur={(e) => {
            setIsFocused(false)
            focusRingOnBlur?.(e)
          }}
          caretHidden
          autoFocus
        />
        {isError && (
          <Typography colors="danger" className="mt-3 py-1" size="xs">
            {errorMessage || <FormattedMessage id="login.code-input.check-email" />}
          </Typography>
        )}
      </View>
    </View>
  )
}
